import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useControllers } from "../state/controllers";
import { useProjectStore } from "../state/projectStore";
import { usePlaybackStore } from "../state/playbackStore";
import { songKey, loadScore, saveScore, deleteScore, DETECTOR_VERSION, type StoredScore } from "../pdf/pdfStore";
import { renderPdf, type RenderedPage } from "../pdf/renderPdf";
import { buildSheetMap } from "../core/pdf/sheetMap";
import { UploadIcon, TrashIcon } from "./icons";
import { useT } from "../i18n";

const MAX_PDF_MB = 30;

// PDF sheet view: the user's own PDF (kept only in this browser), measures auto-detected on each page,
// the playing measure highlighted and followed, tap a measure to play from there. Bars map in playing
// order: repeats and 1st/2nd endings unrolled, multi-measure rests count their bars, the analysis'
// pickup bar is skipped.
export function PdfScore() {
  const c = useControllers();
  const t = useT();
  const source = useProjectStore((s) => s.source);
  const title = useProjectStore((s) => s.title);
  const chart = useProjectStore((s) => s.chartMeasures);
  const currentMeasure = usePlaybackStore((s) => s.currentMeasure);
  const isPlaying = usePlaybackStore((s) => s.isPlaying);
  const key = songKey(source, title);

  const [stored, setStored] = useState<StoredScore | null | undefined>(undefined);   // undefined = loading
  const [pages, setPages] = useState<RenderedPage[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // load this song's PDF from IndexedDB and rebuild the page images. Boxes come from storage unless they
  // were made by an older detector — then detect again and save, so nobody has to re-upload.
  useEffect(() => {
    let cancelled = false;
    setStored(undefined); setPages([]); setErr("");
    if (!key) { setStored(null); return; }
    loadScore(key).then(async (rec) => {
      if (cancelled) return;
      if (!rec) { setStored(null); return; }
      setStored(rec); setBusy(true);
      try {
        const stale = rec.detector !== DETECTOR_VERSION;
        const imgs = await renderPdf(rec.pdf, stale);
        if (cancelled) return;
        if (stale) {
          setPages(imgs);
          const next = { ...rec, pages: imgs.map((p) => ({ measures: p.measures, aspect: p.aspect })), detector: DETECTOR_VERSION };
          setStored(next);
          saveScore(next).catch(() => { /* keep in memory */ });
        } else {
          setPages(imgs.map((p, i) => ({ ...p, measures: rec.pages[i]?.measures ?? [] })));
        }
      } catch { if (!cancelled) setErr(t("pdf.err")); }
      finally { if (!cancelled) setBusy(false); }
    }).catch(() => { if (!cancelled) setStored(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => () => pages.forEach((p) => URL.revokeObjectURL(p.url)), [pages]);

  const boxes = useMemo(() => pages.flatMap((p, page) => p.measures.map((m) => ({ ...m, page }))), [pages]);
  // sheet bars ↔ audio bars (repeats/voltas unrolled, multi-measure rests, the analysis' pickup bar)
  const beatsPerBar = useProjectStore((s) => s.tempoGrid?.beatsPerBar ?? 4);
  const musicStartBar = useProjectStore((s) => s.musicStartBar);
  const map = useMemo(
    () => buildSheetMap(boxes, chart.map((m) => m.beats.length), beatsPerBar, chart.map((m) => m.beats[0]?.chord ?? null), musicStartBar),
    [boxes, chart, beatsPerBar, musicStartBar],
  );
  const nScore = map.sheetBars;
  const cur = map.boxForAudio(currentMeasure);   // box index

  // keep the playing measure in view (same band as the chord grid: below the topbar, above the dock)
  useLayoutEffect(() => {
    if (!isPlaying || cur == null) return;
    const el = wrapRef.current?.querySelector<HTMLElement>(`[data-s="${cur}"]`);
    const area = el?.closest<HTMLElement>(".scroll-area");
    if (!el || !area) return;
    const r = el.getBoundingClientRect(), a = area.getBoundingClientRect();
    if (r.top >= a.top + 80 && r.bottom <= a.bottom - 150) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    area.scrollTo({ top: area.scrollTop + r.top - a.top - 120, behavior: reduce ? "auto" : "smooth" });
  }, [cur, isPlaying]);

  const persist = async (next: StoredScore) => { setStored(next); try { await saveScore(next); } catch { /* quota/private mode: keep in memory */ } };

  async function onFile(file: File) {
    if (!key) return;
    setErr("");
    if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) { setErr(t("pdf.notpdf")); return; }
    if (file.size > MAX_PDF_MB * 1024 * 1024) { setErr(t("pdf.toobig", { n: MAX_PDF_MB })); return; }
    setBusy(true);
    try {
      const buf = await file.arrayBuffer();
      const imgs = await renderPdf(buf, true);
      setPages(imgs);
      await persist({ key, name: file.name, pdf: buf, pages: imgs.map((p) => ({ measures: p.measures, aspect: p.aspect })), detector: DETECTOR_VERSION, updatedAt: Date.now() });
    } catch { setErr(t("pdf.err")); }
    finally { setBusy(false); }
  }

  const tap = (box: number) => {
    const s = map.audioForBox(box, Math.max(0, currentMeasure));   // repeated bar → the pass at/after the cursor
    if (!stored || s == null) return;           // sheet longer than the analysis: nothing to play there
    const time = chart[s].startTime;
    c.activeSource().seek(time);
    usePlaybackStore.getState().setCurrentMeasure(s);
    usePlaybackStore.getState().setCurrentBeatTime(time);
  };

  if (!key) return <div className="score-empty">{t("pdf.nosong")}</div>;
  if (stored === undefined) return <div className="score-empty">{t("pdf.loading")}</div>;

  const picker = (
    <input ref={fileRef} type="file" accept="application/pdf,.pdf" hidden
      onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.currentTarget.value = ""; }} />
  );

  if (!stored) {
    return (
      <div className={"pdf-drop" + (drag ? " over" : "")} role="button" tabIndex={0}
        onClick={() => fileRef.current?.click()}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fileRef.current?.click(); } }}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }}>
        <UploadIcon size={28} />
        <div className="pdf-drop-title">{busy ? t("pdf.reading") : t("pdf.upload")}</div>
        <div className="pdf-drop-sub">{t("pdf.upload_sub")}</div>
        {err && <div className="status err">{err}</div>}
        {picker}
      </div>
    );
  }

  return (
    <div className="pdf-score" ref={wrapRef}>
      <div className="pdf-bar">
        <div className="pdf-meta">
          <div className="pdf-name">{stored.name}</div>
          <div className="pdf-count">
            {t("pdf.counts", { s: nScore, a: chart.length })}
            {map.offsetFrom === "chords" && ` · ${t("pdf.offset", { n: map.offset + 1 })}`}
          </div>
        </div>
        <button className="pdf-btn" onClick={() => fileRef.current?.click()}><UploadIcon size={15} /><span>{t("pdf.replace")}</span></button>
        <button className={"pdf-btn icon" + (confirmDel ? " danger" : "")} aria-label={t("pdf.delete")} title={t("pdf.delete")}
          onClick={async () => {
            if (!confirmDel) { setConfirmDel(true); setTimeout(() => setConfirmDel(false), 3000); return; }
            await deleteScore(key).catch(() => {}); setStored(null); setPages([]); setConfirmDel(false);
          }}>
          <TrashIcon size={15} />{confirmDel && <span>{t("pdf.delete_confirm")}</span>}
        </button>
        {picker}
      </div>
      {busy && <div className="status analyzing"><span className="spinner" />{t("pdf.reading")}</div>}
      {err && <div className="status err">{err}</div>}
      {!busy && pages.length > 0 && !boxes.length && <div className="pdf-warn">{t("pdf.nomeasures")}</div>}
      <div className="pdf-pages">
        {pages.map((p, pi) => (
          <div key={pi} className="pdf-page" style={{ aspectRatio: `1 / ${p.aspect}` }}>
            <img src={p.url} alt={t("pdf.page", { n: pi + 1 })} draggable={false} />
            {boxes.map((b, s) => b.page !== pi ? null : (
              <button key={s} data-s={s} className={"pdf-m" + (s === cur ? " on" : "")}
                style={{ left: `${b.x0 * 100}%`, top: `${b.y0 * 100}%`, width: `${(b.x1 - b.x0) * 100}%`, height: `${(b.y1 - b.y0) * 100}%` }}
                onClick={() => tap(s)} aria-label={t("pdf.measure", { n: s + 1 })} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
